/**
 * Configured v2 API client.
 *
 * Single entrypoint for all dashboard → /api/v2 traffic.
 * Handles auth injection, account context, request IDs and error
 * normalisation into the v2 error envelope.
 *
 * When the generated `openapi-fetch` client is available, this module
 * will wrap it.  Until then it exposes a thin typed `fetchV2` helper
 * that all v2 hooks should use instead of raw `fetch` / `axios`.
 */

import { ApiV2Error, parseV2Error } from "./errors";
import { getSessionToken, handleSessionExpired } from "../dashboard-session";

// ── Configuration ───────────────────────────────────────────────────────

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";

function getToken(): string | null {
  return getSessionToken();
}

/**
 * In-memory account ID.  Set by the session bootstrap and read by every
 * v2 request.  Do **not** store the source of truth here — this is
 * derived from the backend session response.
 */
let _accountId: string | null = null;

export function setAccountId(id: string | null): void {
  _accountId = id;
}

export function getAccountId(): string | null {
  return _accountId;
}

// ── Core fetch wrapper ──────────────────────────────────────────────────

export interface FetchV2Options extends Omit<RequestInit, "headers"> {
  /** Additional headers merged with defaults. */
  headers?: Record<string, string>;
  /** Skip authentication (for public v2 endpoints if any). */
  noAuth?: boolean;
}

/**
 * Low-level v2 fetch.  All v2 hooks should use this, never raw `fetch`.
 *
 * - Injects `Authorization`, `X-Account-Id`, `X-Request-ID`.
 * - Normalises non-2xx responses into `ApiV2Error`.
 * - Returns parsed JSON for 2xx, `undefined` for 204.
 */
export async function fetchV2<T = unknown>(
  path: string,
  options: FetchV2Options = {},
): Promise<T> {
  const { headers: extraHeaders, noAuth, ...init } = options;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...extraHeaders,
  };

  // FormData carries its own multipart content type, including a boundary the browser
  // generates. Sending our JSON default instead produces a body the server cannot parse.
  if (init.body instanceof FormData) {
    delete headers["Content-Type"];
  }

  if (!noAuth) {
    const token = getToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  if (_accountId) {
    headers["X-Account-Id"] = _accountId;
  }

  // Generate a request ID for correlation in logs / error responses.
  headers["X-Request-ID"] = crypto.randomUUID();

  const res = await fetch(`${BASE_URL}/api/v2${path}`, { ...init, headers });

  // ── Handle 401 → redirect to login ──
  if (res.status === 401) {
    const err = await parseV2Error(res);
    // Clear stale token and redirect.
    handleSessionExpired();
    throw err ?? new ApiV2Error(401, {
      error: { code: "unauthorized", message: "Session expired." },
    });
  }

  // ── Handle other non-2xx ──
  if (!res.ok) {
    const err = await parseV2Error(res);
    throw err ?? new ApiV2Error(res.status, {
      error: {
        code: "unknown_error",
        message: `${res.status} ${res.statusText}`,
      },
    });
  }

  // 204 No Content
  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

// ── Convenience HTTP-method helpers ─────────────────────────────────────

export const v2 = {
  get: <T = unknown>(path: string, opts?: FetchV2Options) =>
    fetchV2<T>(path, { method: "GET", ...opts }),

  post: <T = unknown>(path: string, body?: unknown, opts?: FetchV2Options) =>
    fetchV2<T>(path, {
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...opts,
    }),

  put: <T = unknown>(path: string, body?: unknown, opts?: FetchV2Options) =>
    fetchV2<T>(path, {
      method: "PUT",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...opts,
    }),

  patch: <T = unknown>(path: string, body?: unknown, opts?: FetchV2Options) =>
    fetchV2<T>(path, {
      method: "PATCH",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...opts,
    }),

  delete: <T = unknown>(path: string, opts?: FetchV2Options) =>
    fetchV2<T>(path, { method: "DELETE", ...opts }),
};
